/**
 * Session lifecycle helpers shared by the API client, the auth provider, the shell's idle
 * timer and the sign-in page (E-UX).
 */

/** sessionStorage key: a one-line reason shown on the sign-in page after an automatic sign-out. */
export const SIGNED_OUT_REASON_KEY = 'bigroi.signedOutReason'

/** DOM event fired by the API client when an authenticated request comes back 401. */
export const SESSION_EXPIRED_EVENT = 'bigroi:session-expired'

/**
 * Idle sign-out. After IDLE_MINUTES with no keyboard, pointer, scroll or touch activity in ANY tab
 * of this app, the user is warned; WARN_SECONDS later they are signed out. Configurable at build
 * time with VITE_IDLE_MINUTES (0 disables). The server session lifetime (SESSION_LIFETIME) is the
 * hard limit; this is the shared-computer safeguard in front of it.
 */
export const IDLE_MINUTES = (() => {
  const v = Number(import.meta.env.VITE_IDLE_MINUTES ?? 30)
  return Number.isFinite(v) && v >= 0 ? v : 30
})()
export const WARN_SECONDS = 120

/** localStorage key used to share "last activity" across tabs, so one busy tab keeps all alive. */
export const LAST_ACTIVITY_KEY = 'bigroi.lastActivity'

export function rememberSignOutReason(reason: string): void {
  try { sessionStorage.setItem(SIGNED_OUT_REASON_KEY, JSON.stringify({ reason, at: Date.now() })) } catch { /* storage unavailable */ }
}

/** The reason of the last automatic sign-out in this tab, if it happened in the last 15 minutes. */
export function readSignOutReason(): string | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(SIGNED_OUT_REASON_KEY) ?? 'null') as { reason?: string; at?: number } | null
    return v?.reason && v.at && Date.now() - v.at < 15 * 60_000 ? v.reason : null
  } catch { return null }
}

export function clearSignOutReason(): void {
  try { sessionStorage.removeItem(SIGNED_OUT_REASON_KEY) } catch { /* storage unavailable */ }
}

/** Paths whose 401 is an expected answer (not a dead session): sign-in, 2FA, reset, invite, /me probe. */
export function isAuthProbe(url: string | undefined): boolean {
  if (!url) return false
  return /\/api\/v1\/(auth\/(login|logout|2fa\/verify|forgot-password|reset-password|invite\/)|me$|public\/)|\/sanctum\/csrf-cookie/.test(url)
}
